import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, CheckCircle2, AlertCircle, Sparkles, Info, Camera,
} from "lucide-react";
import useSEO from "@/lib/useSEO";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { usePublicServices } from "@/contexts/ServicesContext";

/* ---- Service catalog: fetched live from Admin -> Services (single source of truth) ---- */
/* These constants are only used as a final fallback if the API is unreachable. */
const FALLBACK_SERVICES = [
  "Residential cleaning",
  "Move-in cleaning",
  "Move-out cleaning",
  "Pre-listing cleaning",
  "Property management turnover cleaning",
  "Biweekly cleaning",
  "Monthly cleaning",
  "Commercial cleaning",
  "Office cleaning",
  "Junk removal",
  "Carpet cleaning",
  "Yard cleanup / mowing / edging",
  "Special request",
];

const SPECIAL_TRIGGER = "Special request";

const RES_CONDITIONS = ["Light", "Standard", "Heavy", "Needs inspection"];
const COM_FREQ = ["One-time", "Weekly", "Biweekly", "Monthly", "Custom"];
const COM_TIME = ["During business hours", "After hours", "Weekend", "Flexible"];
const COM_AREAS = [
  "Reception / lobby", "Offices / workstations", "Restrooms",
  "Breakroom / kitchen", "Common areas", "Retail floor", "Showroom",
];

const FALLBACK_ADDONS = [
  "Inside fridge", "Inside oven", "Inside kitchen cabinets",
  "Blinds", "Window sills", "Garage cleaning", "Carpet cleaning",
];

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A] transition-colors";
const inputErrClass = inputClass + " border-rose-300 ring-1 ring-rose-200";
const labelClass = "block text-sm font-semibold text-[#0F172A] mb-1.5";

export default function Estimate() {
  useSEO({ slug: "request-estimate", title: "Request a Cleaning Estimate in Sacramento | P & A Cleaning" });

  const { services: liveServices } = usePublicServices();

  // Build picker list + per-name category map from live services.
  // Always include the "Special request" sentinel so customers can still pick it.
  const serviceOptions = (() => {
    if (!liveServices || liveServices.length === 0) return FALLBACK_SERVICES;
    const names = liveServices.map((s) => s.name);
    return names.includes(SPECIAL_TRIGGER) ? names : [...names, SPECIAL_TRIGGER];
  })();
  const categoryByName = Object.fromEntries(
    (liveServices || []).map((s) => [s.name, String(s.category || "").toLowerCase()]),
  );

  const isResidential = (name) => {
    const cat = categoryByName[name];
    if (cat) return cat === "residential" || cat === "recurring";
    // Fallback heuristic when service isn't in live list (e.g. legacy stored picks).
    return /move|listing|property|biweekly|monthly|residential/i.test(name);
  };
  const isCommercial = (name) => {
    const cat = categoryByName[name];
    if (cat) return cat === "commercial";
    return /commercial|office/i.test(name);
  };

  const [addons, setAddons] = useState(FALLBACK_ADDONS);
  useEffect(() => {
    api.get("/public/addons").then((r) => {
      const list = Array.isArray(r.data) ? r.data.map((a) => a.name).filter(Boolean) : [];
      if (list.length > 0) setAddons(list);
    }).catch(() => {});
  }, []);

  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState({});
  const [topError, setTopError] = useState("");
  const formRef = useRef(null);

  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", city_area: "",
    services_needed: [],
    property_address: "", preferred_date: "", square_footage: "",
    bedrooms: "", bathrooms: "", vacant_or_occupied: "",
    property_condition: "", add_ons_requested: [], access_method: "",
    business_name: "", business_type: "",
    number_of_restrooms: "", cleaning_frequency: "",
    preferred_cleaning_time: "", areas_to_clean: [],
    walkthrough_requested: "",
    special_notes: "",
    consent_confirmed: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) =>
    setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const hasRes = form.services_needed.some(isResidential);
  const hasCom = form.services_needed.some(isCommercial);
  const hasSpecial = form.services_needed.includes(SPECIAL_TRIGGER);

  // ---- Validation
  const errors = {
    full_name: !form.full_name.trim() ? "Required" : "",
    phone: !form.phone.trim() ? "Required" : "",
    email: !form.email.trim() ? "Required" : (!/.+@.+\..+/.test(form.email) ? "Invalid email" : ""),
    city_area: !form.city_area.trim() ? "Required" : "",
    services_needed: form.services_needed.length === 0 ? "Please select at least one service." : "",
    consent_confirmed: !form.consent_confirmed ? "Please confirm that you understand pricing is an estimate only." : "",
  };

  const fieldRefs = useRef({});
  const setRef = (key) => (el) => { fieldRefs.current[key] = el; };

  const scrollToField = (key) => {
    const el = fieldRefs.current[key];
    if (!el) return;
    // Smooth scroll with offset for sticky 64-80px navbar
    const y = el.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const submit = async (e) => {
    e?.preventDefault();
    // Mark everything touched so errors show
    setTouched(Object.keys(errors).reduce((acc, k) => ({ ...acc, [k]: true }), {}));
    const order = ["full_name", "phone", "email", "city_area", "services_needed", "consent_confirmed"];
    const firstBad = order.find((k) => errors[k]);
    if (firstBad) {
      setTopError("Please complete the required fields highlighted below.");
      scrollToField(firstBad);
      return;
    }
    setTopError("");

    setBusy(true);
    try {
      // Derive residential_or_commercial label
      const segment = hasRes && hasCom ? "Both" : hasCom ? "Commercial" : hasRes ? "Residential" : "Other";
      const payload = { ...form, residential_or_commercial: segment };
      await api.post("/estimates", payload);
      setSubmitted(true);
      toast.success("Request sent");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-gradient-to-b from-[#F8FAFC] to-white min-h-[70vh] py-20" data-testid="estimate-success">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#34D399]/15 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-[#10B981]" />
          </div>
          <h1 className="mt-6 text-3xl sm:text-4xl font-display font-bold text-[#0F172A]">Thank you!</h1>
          <p className="mt-4 text-[#475569] text-lg leading-relaxed">
            We received your request. We'll review your details and contact you with a per-job estimate and availability.
          </p>
          <p className="mt-3 text-sm text-[#475569]">
            Need to share additional photos after submitting your request?
            Email them to <a href="mailto:info@pacleans.com" className="text-[#1E3A8A] font-semibold">info@pacleans.com</a> and include your name so we can match them with your estimate.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/" data-testid="estimate-success-home" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF]">Back to Home</Link>
            <Link to="/services" data-testid="estimate-success-services" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#1E3A8A] font-semibold border border-slate-200 hover:bg-slate-50">See all services <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-estimate">
      <section className="bg-gradient-to-b from-[#F8FAFC] to-white py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-[#1E3A8A] shadow-soft">
            <Sparkles className="w-3.5 h-3.5 text-[#34D399]" /> Start Your Estimate
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl font-display font-bold text-[#0F172A] tracking-tight">
            Request an <span className="gradient-text">Estimate</span>
          </h1>
          <p className="mt-4 text-[#475569] text-lg">
            Quick details below, and we'll come back with a per-job quote and availability.
          </p>
        </div>
      </section>

      <section className="pb-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {topError && (
            <div data-testid="estimate-top-error" className="mb-5 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-800">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span className="text-sm font-medium">{topError}</span>
            </div>
          )}

          <form
            ref={formRef}
            onSubmit={submit}
            noValidate
            className="bg-white rounded-3xl border border-slate-100 shadow-soft p-6 sm:p-10 space-y-6"
            data-testid="estimate-form"
          >
            {/* Basic info */}
            <fieldset className="space-y-5">
              <legend className="text-2xl font-display font-semibold text-[#0F172A] mb-2">Basic info</legend>

              <div className="grid sm:grid-cols-2 gap-4">
                <div ref={setRef("full_name")}>
                  <label className={labelClass}>Full name *</label>
                  <input
                    data-testid="estimate-full-name"
                    type="text" value={form.full_name}
                    onChange={(e) => set("full_name", e.target.value)}
                    onBlur={() => setTouched({ ...touched, full_name: true })}
                    className={touched.full_name && errors.full_name ? inputErrClass : inputClass}
                    placeholder="Your name"
                  />
                  {touched.full_name && errors.full_name && <p className="mt-1 text-xs text-rose-600">{errors.full_name}</p>}
                </div>
                <div ref={setRef("phone")}>
                  <label className={labelClass}>Phone *</label>
                  <input
                    data-testid="estimate-phone"
                    type="tel" inputMode="tel" value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    onBlur={() => setTouched({ ...touched, phone: true })}
                    className={touched.phone && errors.phone ? inputErrClass : inputClass}
                    placeholder="(916) 555-0123"
                  />
                  {touched.phone && errors.phone && <p className="mt-1 text-xs text-rose-600">{errors.phone}</p>}
                </div>
                <div ref={setRef("email")}>
                  <label className={labelClass}>Email *</label>
                  <input
                    data-testid="estimate-email"
                    type="email" inputMode="email" value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    onBlur={() => setTouched({ ...touched, email: true })}
                    className={touched.email && errors.email ? inputErrClass : inputClass}
                    placeholder="you@email.com"
                  />
                  {touched.email && errors.email && <p className="mt-1 text-xs text-rose-600">{errors.email}</p>}
                </div>
                <div ref={setRef("city_area")}>
                  <label className={labelClass}>City / area *</label>
                  <input
                    data-testid="estimate-city"
                    type="text" value={form.city_area}
                    onChange={(e) => set("city_area", e.target.value)}
                    onBlur={() => setTouched({ ...touched, city_area: true })}
                    className={touched.city_area && errors.city_area ? inputErrClass : inputClass}
                    placeholder="Sacramento, Elk Grove, etc."
                  />
                  {touched.city_area && errors.city_area && <p className="mt-1 text-xs text-rose-600">{errors.city_area}</p>}
                </div>
              </div>
            </fieldset>

            {/* Services needed (multi-select) */}
            <fieldset ref={setRef("services_needed")}>
              <legend className={labelClass}>Services needed *</legend>
              <p className="text-xs text-[#475569] mb-3">Select all services you need.</p>
              <div className={`grid sm:grid-cols-2 gap-2 ${touched.services_needed && errors.services_needed ? "ring-2 ring-rose-200 rounded-2xl p-2 -m-2" : ""}`}>
                {serviceOptions.map((opt) => {
                  const checked = form.services_needed.includes(opt);
                  return (
                    <label
                      key={opt}
                      data-testid={`estimate-service-${opt.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer text-base font-medium transition-all min-h-[48px] ${
                        checked
                          ? "bg-[#1E3A8A]/5 border-[#1E3A8A] text-[#1E3A8A]"
                          : "bg-white text-[#0F172A] border-slate-200 hover:border-[#1E3A8A]/50 hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="checkbox" checked={checked}
                        onChange={() => { toggleArr("services_needed", opt); setTouched({ ...touched, services_needed: true }); }}
                        className="w-5 h-5 rounded text-[#1E3A8A] focus:ring-[#1E3A8A]"
                      />
                      <span>{opt}</span>
                    </label>
                  );
                })}
              </div>
              {touched.services_needed && errors.services_needed && (
                <p className="mt-2 text-sm text-rose-600 font-medium">{errors.services_needed}</p>
              )}
            </fieldset>

            {/* Residential details */}
            {hasRes && (
              <fieldset className="space-y-5 border-t border-slate-100 pt-6" data-testid="estimate-residential-section">
                <legend className="text-xl font-display font-semibold text-[#0F172A] mb-2">Residential details</legend>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Property address</label>
                    <input data-testid="estimate-res-address" type="text" value={form.property_address}
                      onChange={(e) => set("property_address", e.target.value)} className={inputClass}
                      placeholder="123 Main St, Sacramento, CA" />
                  </div>
                  <div>
                    <label className={labelClass}>Preferred date</label>
                    <input data-testid="estimate-res-date" type="date" value={form.preferred_date}
                      onChange={(e) => set("preferred_date", e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Square footage</label>
                    <input data-testid="estimate-res-sqft" type="text" inputMode="numeric" value={form.square_footage}
                      onChange={(e) => set("square_footage", e.target.value)} className={inputClass}
                      placeholder="e.g. 1,400" />
                  </div>
                  <div>
                    <label className={labelClass}>Bedrooms</label>
                    <input data-testid="estimate-res-bedrooms" type="text" inputMode="numeric" value={form.bedrooms}
                      onChange={(e) => set("bedrooms", e.target.value)} className={inputClass} placeholder="0" />
                  </div>
                  <div>
                    <label className={labelClass}>Bathrooms</label>
                    <input data-testid="estimate-res-bathrooms" type="text" inputMode="numeric" value={form.bathrooms}
                      onChange={(e) => set("bathrooms", e.target.value)} className={inputClass} placeholder="0" />
                  </div>
                  <div>
                    <label className={labelClass}>Vacant or occupied</label>
                    <select data-testid="estimate-res-occupancy" value={form.vacant_or_occupied}
                      onChange={(e) => set("vacant_or_occupied", e.target.value)} className={inputClass}>
                      <option value="">Select…</option><option>Vacant</option><option>Occupied</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Property condition</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {RES_CONDITIONS.map((c) => (
                      <button type="button" key={c}
                        onClick={() => set("property_condition", c)}
                        data-testid={`estimate-res-condition-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`px-3 py-3 rounded-xl border text-base font-medium transition-all min-h-[48px] ${
                          form.property_condition === c
                            ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                            : "bg-white text-[#0F172A] border-slate-200 hover:border-[#1E3A8A]/50"
                        }`}>{c}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Add-ons needed</label>
                  <p className="text-xs text-[#475569] mb-2">Select all that apply.</p>
                  <div className="flex flex-wrap gap-2">
                    {addons.map((a) => (
                      <button type="button" key={a}
                        onClick={() => toggleArr("add_ons_requested", a)}
                        data-testid={`estimate-addon-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`px-4 py-2.5 rounded-full border text-sm font-semibold transition-all min-h-[40px] ${
                          form.add_ons_requested.includes(a)
                            ? "bg-[#A78BFA] text-white border-[#A78BFA]"
                            : "bg-white text-[#0F172A] border-slate-200 hover:border-[#A78BFA]/50"
                        }`}>
                        {form.add_ons_requested.includes(a) ? "✓ " : "+ "}{a}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Access method</label>
                  <input data-testid="estimate-res-access" type="text" value={form.access_method}
                    onChange={(e) => set("access_method", e.target.value)} className={inputClass}
                    placeholder="Lockbox, keypad, owner present, etc." />
                </div>
              </fieldset>
            )}

            {/* Commercial details */}
            {hasCom && (
              <fieldset className="space-y-5 border-t border-slate-100 pt-6" data-testid="estimate-commercial-section">
                <legend className="text-xl font-display font-semibold text-[#0F172A] mb-2">Commercial details</legend>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Business name</label>
                    <input data-testid="estimate-com-business-name" type="text" value={form.business_name}
                      onChange={(e) => set("business_name", e.target.value)} className={inputClass} placeholder="Your business" />
                  </div>
                  <div>
                    <label className={labelClass}>Business type</label>
                    <input data-testid="estimate-com-business-type" type="text" value={form.business_type}
                      onChange={(e) => set("business_type", e.target.value)} className={inputClass}
                      placeholder="Office, retail, salon, etc." />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Property address</label>
                    <input data-testid="estimate-com-address" type="text" value={form.property_address}
                      onChange={(e) => set("property_address", e.target.value)} className={inputClass}
                      placeholder="123 Business Ave, Sacramento, CA" />
                  </div>
                  <div>
                    <label className={labelClass}>Approx. square footage</label>
                    <input data-testid="estimate-com-sqft" type="text" inputMode="numeric" value={form.square_footage}
                      onChange={(e) => set("square_footage", e.target.value)} className={inputClass} placeholder="e.g. 2,000" />
                  </div>
                  <div>
                    <label className={labelClass}>Number of restrooms</label>
                    <input data-testid="estimate-com-restrooms" type="text" inputMode="numeric" value={form.number_of_restrooms}
                      onChange={(e) => set("number_of_restrooms", e.target.value)} className={inputClass} placeholder="0" />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Cleaning frequency</label>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {COM_FREQ.map((f) => (
                      <button type="button" key={f}
                        onClick={() => set("cleaning_frequency", f)}
                        data-testid={`estimate-com-freq-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`px-3 py-3 rounded-xl border text-base font-medium transition-all min-h-[48px] ${
                          form.cleaning_frequency === f
                            ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                            : "bg-white text-[#0F172A] border-slate-200 hover:border-[#1E3A8A]/50"
                        }`}>{f}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Preferred cleaning time</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {COM_TIME.map((t) => (
                      <button type="button" key={t}
                        onClick={() => set("preferred_cleaning_time", t)}
                        data-testid={`estimate-com-time-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`px-3 py-3 rounded-xl border text-base font-medium transition-all min-h-[48px] ${
                          form.preferred_cleaning_time === t
                            ? "bg-[#A78BFA] text-white border-[#A78BFA]"
                            : "bg-white text-[#0F172A] border-slate-200 hover:border-[#A78BFA]/50"
                        }`}>{t}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Areas to clean</label>
                  <div className="flex flex-wrap gap-2">
                    {COM_AREAS.map((a) => (
                      <button type="button" key={a}
                        onClick={() => toggleArr("areas_to_clean", a)}
                        data-testid={`estimate-com-area-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`px-4 py-2.5 rounded-full border text-sm font-semibold min-h-[40px] ${
                          form.areas_to_clean.includes(a)
                            ? "bg-[#34D399] text-[#0F172A] border-[#34D399]"
                            : "bg-white text-[#0F172A] border-slate-200 hover:border-[#34D399]/60"
                        }`}>
                        {form.areas_to_clean.includes(a) ? "✓ " : "+ "}{a}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Walkthrough requested</label>
                  <div className="grid grid-cols-3 gap-2">
                    {["Yes", "No", "Not sure"].map((w) => (
                      <button type="button" key={w}
                        onClick={() => set("walkthrough_requested", w)}
                        data-testid={`estimate-com-walkthrough-${w.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`px-3 py-3 rounded-xl border text-base font-medium min-h-[48px] ${
                          form.walkthrough_requested === w
                            ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                            : "bg-white text-[#0F172A] border-slate-200 hover:border-[#1E3A8A]/50"
                        }`}>{w}</button>
                    ))}
                  </div>
                </div>
              </fieldset>
            )}

            {/* Special notes (always show, larger when Special request selected) */}
            <fieldset className="border-t border-slate-100 pt-6" data-testid={hasSpecial ? "estimate-special-section" : undefined}>
              <label className={labelClass}>{hasSpecial ? "Tell us about your special request" : "Special notes"}</label>
              <textarea
                data-testid="estimate-special-notes"
                rows={hasSpecial ? 5 : 3}
                value={form.special_notes}
                onChange={(e) => set("special_notes", e.target.value)}
                className={inputClass}
                placeholder={hasSpecial
                  ? "What do you need? Tell us anything that helps us prepare an accurate quote."
                  : "Anything we should know? Access, pets, focus areas…"}
              />
            </fieldset>

            {/* Photo note (replaces upload per request) */}
            <div className="rounded-2xl border border-slate-100 bg-[#F8FAFC] p-5 flex items-start gap-3" data-testid="estimate-photo-note">
              <Camera className="w-5 h-5 text-[#A78BFA] mt-0.5" />
              <p className="text-sm text-[#0F172A]">
                Need to share additional photos after submitting your request?
                Email them to{" "}
                <a href="mailto:info@pacleans.com" className="text-[#1E3A8A] font-semibold underline">info@pacleans.com</a>{" "}
                and include your name so we can match them with your estimate.
              </p>
            </div>

            {/* Consent */}
            <fieldset
              ref={setRef("consent_confirmed")}
              className={`rounded-2xl p-5 ${touched.consent_confirmed && errors.consent_confirmed ? "border-2 border-rose-200 bg-rose-50/60" : "border border-slate-100 bg-[#F8FAFC]"}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <Info className="w-5 h-5 text-[#1E3A8A] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#475569] leading-relaxed">
                  Pricing is per-job, not hourly. Final pricing may change after inspection or review of photos.
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer min-h-[48px]">
                <input
                  type="checkbox"
                  data-testid="estimate-consent"
                  checked={form.consent_confirmed}
                  onChange={(e) => { set("consent_confirmed", e.target.checked); setTouched({ ...touched, consent_confirmed: true }); }}
                  className="mt-1 w-5 h-5 rounded border-slate-300 text-[#1E3A8A] focus:ring-[#1E3A8A]"
                />
                <span className="text-base text-[#0F172A] leading-relaxed">
                  I understand pricing is an estimate only and final pricing may change after inspection or review of photos.
                </span>
              </label>
              {touched.consent_confirmed && errors.consent_confirmed && (
                <p className="mt-2 text-sm text-rose-700 font-medium">{errors.consent_confirmed}</p>
              )}
            </fieldset>

            {/* Action row */}
            <div className="flex items-center justify-between gap-3 pt-2">
              <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0F172A]">
                <ArrowLeft className="w-4 h-4" /> Back home
              </Link>
              <button
                type="submit"
                disabled={busy}
                data-testid="estimate-submit"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#34D399] text-[#0F172A] font-semibold hover:bg-[#10B981] transition-colors shadow-soft disabled:opacity-50"
              >
                {busy ? "Sending…" : "Submit Request"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
